import { useEffect, useState } from 'react'
import { doc, onSnapshot, setDoc, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { DEFAULT_PARLENT_WATCH_MODELS } from '@/lib/watch-models'

interface AppSettingsData {
  android: {
    version: string
    buildNumber: number
    minVersion: string
    forceUpdate: boolean
    updateMessage: string
    updateTitle: string
    storeUrl: string
    packageName: string
  }
  ios: {
    version: string
    buildNumber: number
    minVersion: string
    forceUpdate: boolean
    updateMessage: string
    updateTitle: string
    storeUrl: string
    bundleId: string
  }
  watchModels: {
    /** Nama jam yang dihitung sebagai jam Parlent di metrik dashboard. */
    parlent: string[]
  }
  maintenanceMode: boolean
  maintenanceTitle: string
  maintenanceMessage: string
  features: {
    enableStrava: boolean
    enableSocial: boolean
    enableStore: boolean
    enableLeaderboard: boolean
    enableChallenges: boolean
    enableAIWatchFace: boolean
  }
  updatedAt?: any
  updatedBy?: string
}

const DEFAULT_SETTINGS: AppSettingsData = {
  android: {
    version: '1.0.0',
    buildNumber: 1,
    minVersion: '1.0.0',
    forceUpdate: false,
    updateMessage: 'A new version is available. Please update to continue.',
    updateTitle: 'Update Available',
    storeUrl: 'https://play.google.com/store/apps/details?id=com.parlent.fit',
    packageName: 'com.parlent.fit',
  },
  ios: {
    version: '1.0.0',
    buildNumber: 1,
    minVersion: '1.0.0',
    forceUpdate: false,
    updateMessage: 'A new version is available. Please update to continue.',
    updateTitle: 'Update Available',
    storeUrl: 'https://apps.apple.com/app/parlent-fit/id1234567890',
    bundleId: 'com.parlent.fit',
  },
  watchModels: {
    parlent: DEFAULT_PARLENT_WATCH_MODELS,
  },
  maintenanceMode: false,
  maintenanceTitle: 'Maintenance Mode',
  maintenanceMessage: 'We are currently performing maintenance. Please try again later.',
  features: {
    enableStrava: true,
    enableSocial: true,
    enableStore: true,
    enableLeaderboard: true,
    enableChallenges: true,
    enableAIWatchFace: true,
  },
}

export function useAppSettings() {
  const [settings, setSettings] = useState<AppSettingsData>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'appSettings', 'current'),
      (doc) => {
        if (doc.exists()) {
          setSettings({ ...DEFAULT_SETTINGS, ...doc.data() } as AppSettingsData)
        } else {
          // Initialize with defaults if not exists
          initializeSettings()
        }
        setLoading(false)
      },
      (err) => {
        console.error('Error fetching app settings:', err)
        setError(err)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  const initializeSettings = async () => {
    try {
      await setDoc(doc(db, 'appSettings', 'current'), {
        ...DEFAULT_SETTINGS,
        updatedAt: Timestamp.now(),
      })
    } catch (err) {
      console.error('Error initializing settings:', err)
    }
  }

  const saveSettings = async (newSettings: Partial<AppSettingsData>) => {
    setSaving(true)
    try {
      await setDoc(
        doc(db, 'appSettings', 'current'),
        {
          ...newSettings,
          updatedAt: Timestamp.now(),
        },
        { merge: true }
      )
      return true
    } catch (err) {
      console.error('Error saving settings:', err)
      setError(err as Error)
      return false
    } finally {
      setSaving(false)
    }
  }

  const updateVersion = async (
    platform: 'android' | 'ios',
    versionData: Partial<AppSettingsData['android']>
  ) => {
    return saveSettings({
      [platform]: {
        ...settings[platform],
        ...versionData,
      },
    })
  }

  const toggleMaintenance = async (enabled: boolean, message?: string) => {
    return saveSettings({
      maintenanceMode: enabled,
      ...(message && { maintenanceMessage: message }),
    })
  }

  const toggleFeature = async (feature: keyof AppSettingsData['features'], enabled: boolean) => {
    return saveSettings({
      features: {
        ...settings.features,
        [feature]: enabled,
      },
    })
  }

  return {
    settings,
    loading,
    error,
    saving,
    saveSettings,
    updateVersion,
    toggleMaintenance,
    toggleFeature,
  }
}
